import React, { useMemo } from "react";
import {
  Card,
  CardContent,
  Box,
  Typography,
  IconButton,
  useTheme,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import {
  AreaChart,
  Area,
  XAxis,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import moment from "moment";

interface DeviationPerformanceChartMobileProps {
  month: number;
  year: number;
  ga_code: string;
  tourCardData?: any;
}

const DeviationPerformanceChartMobile: React.FC<DeviationPerformanceChartMobileProps> = ({
  month = new Date().getMonth() + 1,
  year = new Date().getFullYear(),
  ga_code,
  tourCardData,
}) => {
 const theme = useTheme();
    const minThreshold = tourCardData?.user?.min_deviation_threshold ?? 0;
   const maxThreshold = tourCardData?.user?.max_deviation_threshold ?? 100;
 
   // ✅ Extract and format last 3 months data from API
   const data = useMemo(() => {
     if (!tourCardData?.summary) return [];
 
     const summary = tourCardData.summary;
 
     const last3 = [];
 
     for (let i = 0; i < 3; i++) {
       const date = moment(`${year}-${month}`, "YYYY-MM").subtract(i, "months");
 
       const mm = date.format("MM");
       const yyyy = date.format("YYYY");
 
       // 🔎 find matching API month-year
       const match = summary.find(
         (item: any) => item.month === mm && item.year.toString() === yyyy
       );
 
       last3.push({
         name: date.format("MMM").toUpperCase(),
         value: match ? match.deviation_percentage : 0,
       });
     }
 
     return last3.reverse(); // latest month last
   }, [month, year, tourCardData]);
 
 
   // Safely read current & previous
   const current = data?.[2]?.value ?? 0;
   const previous = data?.[1]?.value ?? 0;
   const difference = Math.abs(current - previous).toFixed(2);
 
   // Determine trend ↑ ↓ →
   let arrow = "→";
   if (current > previous) arrow = "↑";
   else if (current < previous) arrow = "↓";
 
   // Determine control message
   let message = "Maintain Consistency";
 
   if (Number(difference) === 0) {
     message = "No Change, Stay Consistent";
   } else if (current < previous) {
     message = "Good Improvement!";
   } else if (current > previous) {
     message = "Deviation Rising, Take Action!";
   }
 
   // Apply threshold override
   if (current > maxThreshold) {
     message = "⚠ High Deviation – Immediate Focus Needed";
   } else if (current < minThreshold) {
     message = "✔ Excellent, Keep Going!";
   }
 
  return (
    <Card
      sx={{
        borderRadius: 2,
        boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
        bgcolor: "#fff",
      }}
    >
      <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="subtitle1" fontWeight={600}>
            LAST QUARTER/MONTH
          </Typography>
          <IconButton size="small">
            {/* <DownloadIcon fontSize="small" /> */}
          </IconButton>
        </Box>

        {/* Chart */}
        <Box sx={{ height: 140, mb: 1 }}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart 
              data={data} 
              margin={{ top: 10, right: 0, left: 0, bottom: 0 }}
            >
              <defs>
                <linearGradient id="colorOrange" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#FFA726" stopOpacity={0.8} />
                  <stop offset="95%" stopColor="#FFA726" stopOpacity={0.1} />
                </linearGradient>
              </defs>
              <XAxis
                dataKey="name"
                axisLine={false}
                tickLine={false}
                tick={{ fill: "#666", fontSize: 11 }}
                interval={0}
              />
              <Tooltip 
                contentStyle={{
                  backgroundColor: "rgba(255,255,255,0.95)",
                  border: "1px solid #ddd",
                  borderRadius: 4,
                  fontSize: 12,
                }}
              />
              <Area
                type="monotone"
                dataKey="value"
                stroke="#FFA726"
                strokeWidth={2.5}
                fill="url(#colorOrange)"
              />
            </AreaChart>
          </ResponsiveContainer>
        </Box>

        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            display: "block",
            textAlign: "center",
            mb: 2,
            fontSize: "0.7rem",
          }}
        >
          Recommended: Below {minThreshold}%
        </Typography>

        {/* Stats Section */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          sx={{
            bgcolor: "#fff3e0",
            p: 1.5,
            borderRadius: 1,
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Vs Last Month
          </Typography>
          <Box textAlign="right">
            <Typography 
              variant="h4" 
              fontWeight={700} 
              color="#FFA726"
              sx={{ lineHeight: 1 }}
            >
                      {difference}%
            </Typography>
            <Typography
              variant="caption"
              sx={{ 
                color: "#E57373", 
                fontWeight: 700,
                fontSize: "0.65rem",
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
                gap: 0.3,
                mt: 0.3,
              }}
            >
              {arrow} {message}
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default DeviationPerformanceChartMobile;